import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import moment from 'moment';

interface ChatMessage {
    role: 'user' | 'bot';
    text: string;
    buttons?: string;
    timestamp?: string;
}

interface WhatsAppConversationModalProps {
    visible: boolean;
    phone?: string;
    onClose: () => void;
    chatHistoryEndpoint?: string;
    version?: string;
}

// WhatsApp SVG icon
const WaIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787" />
    </svg>
);

// Double tick (read receipt) icon
const DoubleTickIcon = () => (
    <svg viewBox="0 0 16 11" fill="currentColor" className="w-3.5 h-2.5 text-blue-300 inline-block ml-1">
        <path d="M11.071.653a.375.375 0 0 0-.531.02L5.85 5.765 3.84 3.493a.375.375 0 1 0-.567.492l2.302 2.605a.375.375 0 0 0 .549.014L11.09 1.18a.375.375 0 0 0-.019-.528zM15.071.653a.375.375 0 0 0-.531.02L9.85 5.765l-.47-.531a.375.375 0 1 0-.566.492l.75.849a.375.375 0 0 0 .548.014L15.09 1.18a.375.375 0 0 0-.019-.528zM.379 6.1a.375.375 0 0 0 0 .75h1.5a.375.375 0 0 0 0-.75H.379z" />
    </svg>
);

const WhatsAppConversationModal: React.FC<WhatsAppConversationModalProps> = ({ visible, phone, onClose, chatHistoryEndpoint, version }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!visible || !phone) return;
        setLoading(true);
        setMessages([]);
        const senderId = phone.startsWith('91') ? phone : `91${phone.replace(/^0/, '')}`;
        const params = new URLSearchParams({ sender_id: senderId });
        if (version) params.append('version', version);
        axiosInstance.get(`${chatHistoryEndpoint ?? apis.FETCH_WHATSAPP_CHAT_HISTORY}?${params.toString()}`)
            .then(res => {
                if (res.data?.success) setMessages(res.data.chat_history || []);
                else setMessages([]);
            })
            .catch(() => setMessages([]))
            .finally(() => setLoading(false));
    }, [visible, phone, chatHistoryEndpoint, version]);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (!loading) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    if (!visible) return null;

    const displayPhone = phone ? `+${phone.startsWith('91') ? phone : `91${phone}`}` : '';

    const parseMoment = (ts?: string) => {
        if (!ts) return null;
        return moment(ts, ['YYYY-MM-DD HH:mm:ss', 'DD-MM-YYYY HH:mm:ss', moment.ISO_8601], true);
    };

    const formatTime = (ts?: string) => {
        const m = parseMoment(ts);
        if (!m || !m.isValid()) return ts ?? '';
        return m.format('DD-MMM HH:mm');
    };

    const isSameDay = (a?: string, b?: string) => {
        const ma = parseMoment(a);
        const mb = parseMoment(b);
        if (!ma || !mb || !ma.isValid() || !mb.isValid()) return false;
        return ma.isSame(mb, 'day');
    };

    const fmtDateLabel = (ts?: string): string => {
        const m = parseMoment(ts);
        if (!m || !m.isValid()) return '';
        const today = moment().startOf('day');
        if (m.isSame(today, 'day')) return 'Today';
        if (m.isSame(moment(today).subtract(1, 'day'), 'day')) return 'Yesterday';
        return m.format('D MMM YYYY');
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ background: 'rgba(0,0,0,0.72)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="flex flex-col rounded-xl overflow-hidden shadow-2xl"
                style={{ width: 420, maxHeight: '88vh', minHeight: 520 }}
            >
                {/* ── WhatsApp header bar ── */}
                <div
                    className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
                    style={{ background: '#075E54' }}
                >
                    {/* Back / close arrow */}
                    <button onClick={onClose} className="text-white opacity-80 hover:opacity-100 mr-1">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    {/* Avatar circle */}
                    <div
                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                        style={{ background: '#128C7E' }}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                        </svg>
                    </div>
                    {/* Name + online */}
                    <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm leading-none truncate">{displayPhone}</p>
                        <p className="text-green-200 text-[11px] mt-0.5">
                            {messages.length > 0 ? `${messages.length} messages` : 'WhatsApp'}
                        </p>
                    </div>
                    {/* WhatsApp icon top-right */}
                    <span className="text-green-200 opacity-60"><WaIcon /></span>
                </div>

                {/* ── Chat wallpaper background ── */}
                <div
                    className="flex-1 overflow-y-auto px-3 py-3 space-y-1"
                    style={{
                        background: '#ECE5DD',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8b8a0' fill-opacity='0.18'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                >
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 gap-3">
                            <div className="relative w-10 h-10">
                                <div className="absolute inset-0 rounded-full border-4 border-green-200 animate-spin border-t-green-600" />
                            </div>
                            <p className="text-sm text-gray-600 font-medium">Loading conversation…</p>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 gap-3">
                            <div className="w-16 h-16 rounded-full bg-white/60 flex items-center justify-center">
                                <WaIcon />
                            </div>
                            <p className="text-sm text-gray-600 font-medium">No messages found</p>
                            <p className="text-xs text-gray-400">This conversation appears empty</p>
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, idx) => {
                                const isUser = msg.role === 'user';
                                const prevMsg = idx > 0 ? messages[idx - 1] : null;
                                const showDateSep = !prevMsg || !isSameDay(prevMsg.timestamp, msg.timestamp);
                                const dateLabel = fmtDateLabel(msg.timestamp);
                                return (
                                    <React.Fragment key={idx}>
                                        {showDateSep && dateLabel && (
                                            <div className="flex justify-center" style={{ margin: '10px 0 6px' }}>
                                                <span
                                                    style={{
                                                        background: 'rgba(255,255,255,0.75)',
                                                        color: '#555',
                                                        fontSize: 10,
                                                        padding: '3px 12px',
                                                        borderRadius: 12,
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                                        fontWeight: 600,
                                                        letterSpacing: 0.3,
                                                    }}
                                                >
                                                    {dateLabel}
                                                </span>
                                            </div>
                                        )}
                                        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-0.5`}>
                                            <div
                                                className="relative max-w-[82%] px-3 py-2 rounded-lg shadow-sm text-[13px] leading-[1.45]"
                                                style={{
                                                    background: isUser ? '#DCF8C6' : '#FFFFFF',
                                                    borderRadius: isUser
                                                        ? '12px 2px 12px 12px'
                                                        : '2px 12px 12px 12px',
                                                }}
                                            >
                                                {/* Bubble tail */}
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        [isUser ? 'right' : 'left']: -6,
                                                        width: 0,
                                                        height: 0,
                                                        borderStyle: 'solid',
                                                        borderWidth: isUser ? '0 0 8px 8px' : '0 8px 8px 0',
                                                        borderColor: isUser
                                                            ? `transparent transparent transparent #DCF8C6`
                                                            : `transparent #FFFFFF transparent transparent`,
                                                    }}
                                                />
                                                {/* Message text */}
                                                <p className="text-gray-800 whitespace-pre-wrap break-words">{msg.text}</p>
                                                {/* Quick-reply buttons for bot */}
                                                {!isUser && msg.buttons && (
                                                    <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100">
                                                        {msg.buttons.split('|').map((btn, bIdx) => (
                                                            <span
                                                                key={bIdx}
                                                                className="text-[11px] font-medium px-2.5 py-1 rounded-full border text-[#128C7E] border-[#128C7E]/30 bg-[#128C7E]/5"
                                                            >
                                                                {btn.trim()}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {/* Timestamp + read tick */}
                                                <div className="flex items-center gap-0.5 justify-end mt-1">
                                                    <span className="text-[10px] text-gray-400">{formatTime(msg.timestamp)}</span>
                                                    {isUser && <DoubleTickIcon />}
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                            <div ref={bottomRef} />
                        </>
                    )}
                </div>

                {/* ── Locked input bar (read-only, purely decorative) ── */}
                <div
                    className="flex items-center gap-2 px-3 py-2 flex-shrink-0"
                    style={{ background: '#F0F0F0', borderTop: '1px solid #ddd' }}
                >
                    <div className="flex-1 bg-white rounded-full px-4 py-2 text-[12px] text-gray-400 select-none">
                        Read-only view
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhatsAppConversationModal;
